import { CalendarClock, CheckCircle2, Loader2, Mail, UserX } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/base/alert-dialog';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { Spinner } from '@/ui/Spinner';

import {
  useResponderPreCredenciamento,
  useSituacaoPreCredenciamento,
  type SituacaoPreCredenciamento,
} from './api';

/**
 * A resposta do pré-credenciado dentro do prazo (F09, RN-CP-61), no detalhe do
 * veículo do acompanhamento (F06).
 *
 * Confirmar mantém o veículo pré-credenciado e afasta a expiração. Desistir é
 * definitivo: o veículo vai a DESISTENTE e a vaga volta para a lista de
 * espera — por isso pede confirmação explícita. O que a pessoa não pode fazer
 * não aparece (regra 7): sem prazo, sem botão de confirmar.
 *
 * Sem pré-credenciamento para o protocolo (404), o componente não mostra nada.
 */

const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
});

function formatar(valor: string | null): string {
  if (!valor) return '—';
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? '—' : DATA_HORA.format(data).replace(',', ' às');
}

const TEXTO_DO_ACESSO: Record<SituacaoPreCredenciamento['acesso'], string> = {
  PREPARANDO:
    'Estamos criando o seu acesso ao Portal do Credenciado. O e-mail com o link para criar a senha chega em alguns minutos.',
  EMAIL_ENVIADO:
    'Enviamos ao e-mail desta inscrição o link para criar a senha do Portal do Credenciado. Confira também a caixa de spam.',
  LIBERADO:
    'Você já tem conta no Portal do Credenciado: entre com a sua senha de sempre para ver esta inscrição.',
  PENDENTE_DTP:
    'Não conseguimos criar o seu acesso automaticamente. O DTP já foi avisado e vai entrar em contato pelo e-mail desta inscrição.',
};

function novaChave(): string {
  return crypto.randomUUID();
}

export interface RespostaPreCredenciamentoProps {
  protocolo: string;
  /** Chamado depois de confirmar ou desistir, para a página recarregar o que mudou. */
  aoMudar?: () => void;
}

export function RespostaPreCredenciamento({ protocolo, aoMudar }: RespostaPreCredenciamentoProps) {
  const consulta = useSituacaoPreCredenciamento(protocolo);

  if (consulta.isPending) {
    return (
      <Card>
        <CardContent className="py-5">
          <Spinner rotulo="Carregando o prazo de resposta…" />
        </CardContent>
      </Card>
    );
  }
  if (consulta.error?.situacao === 404) return null;
  if (consulta.isError || !consulta.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Não foi possível carregar o prazo de resposta</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>Confira sua conexão e tente de novo em instantes.</p>
          <Button variant="outline" size="sm" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  return <Resposta situacao={consulta.data} protocolo={protocolo} aoMudar={aoMudar} />;
}

function Resposta({
  situacao,
  protocolo,
  aoMudar,
}: {
  situacao: SituacaoPreCredenciamento;
  protocolo: string;
  aoMudar?: () => void;
}) {
  const responder = useResponderPreCredenciamento(protocolo);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  // Uma chave por intenção: repetir o clique não grava duas vezes.
  const [chaveConfirmar] = useState(novaChave);
  const [chaveDesistir] = useState(novaChave);

  function confirmar() {
    responder.mutate(
      { acao: 'confirmar', chaveIdempotencia: chaveConfirmar },
      {
        onSuccess: () => {
          toast.success('Interesse confirmado.');
          aoMudar?.();
        },
        onError: (erro) => toast.error(erro.aviso || 'Não foi possível confirmar. Tente de novo.'),
      },
    );
  }

  function desistir() {
    responder.mutate(
      { acao: 'desistir', chaveIdempotencia: chaveDesistir, motivo: motivo.trim() || undefined },
      {
        onSuccess: () => {
          setDialogoAberto(false);
          toast.success('Desistência registrada.');
          aoMudar?.();
        },
        onError: (erro) => toast.error(erro.aviso || 'Não foi possível registrar. Tente de novo.'),
      },
    );
  }

  const encerrada = situacao.resposta === 'DESISTENTE' || situacao.status !== 'PRE_CREDENCIADO';

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="text-warning h-4 w-4" aria-hidden />
          Sua resposta ao pré-credenciamento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!encerrada && (
          <p>
            Prazo para responder: <strong>{formatar(situacao.prazoRespostaEm)}</strong>
            {situacao.vencido && ' — encerrado'}
          </p>
        )}

        {situacao.resposta === 'CONFIRMADO' && (
          <p className="text-success flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            Interesse confirmado em {formatar(situacao.respondidoEm)}.
          </p>
        )}
        {situacao.resposta === 'DESISTENTE' && (
          <p className="text-muted-foreground flex items-center gap-1.5 font-medium">
            <UserX className="h-4 w-4" aria-hidden />
            Desistência registrada em {formatar(situacao.respondidoEm)}.
          </p>
        )}
        {situacao.resposta === null && situacao.podeConfirmar && (
          <p className="text-muted-foreground">
            Confirme até o prazo que ainda tem interesse. Sem resposta até lá, a inscrição expira e
            a vaga passa para o próximo da lista de espera.
          </p>
        )}
        {situacao.resposta === null && situacao.vencido && !encerrada && (
          <p className="text-muted-foreground">
            O prazo terminou sem resposta. A inscrição será encerrada como expirada.
          </p>
        )}

        {!encerrada && (
          <p className="flex items-start gap-1.5">
            <Mail className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{TEXTO_DO_ACESSO[situacao.acesso]}</span>
          </p>
        )}

        {(situacao.podeConfirmar || situacao.podeDesistir) && (
          <div className="flex flex-col gap-2 sm:flex-row">
            {situacao.podeConfirmar && (
              <Button
                className="h-11 w-full sm:h-10 sm:w-auto"
                disabled={responder.isPending}
                onClick={confirmar}
              >
                {responder.isPending && responder.variables?.acao === 'confirmar' ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                )}
                Confirmar interesse
              </Button>
            )}
            {situacao.podeDesistir && (
              <Button
                variant="outline"
                className="text-destructive h-11 w-full sm:h-10 sm:w-auto"
                disabled={responder.isPending}
                onClick={() => setDialogoAberto(true)}
              >
                <UserX className="h-4 w-4" aria-hidden />
                Desistir
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desistir do credenciamento deste veículo?</AlertDialogTitle>
            <AlertDialogDescription>
              A desistência não pode ser desfeita. A vaga passa para o próximo da lista de espera, e
              o acesso ao Portal do Credenciado por esta inscrição deixa de valer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="motivo-desistencia">Motivo (opcional)</Label>
            <Textarea
              id="motivo-desistencia"
              maxLength={500}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={responder.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={responder.isPending}
              onClick={(evento) => {
                evento.preventDefault();
                desistir();
              }}
            >
              {responder.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Desistir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
